import { appRoutes } from '@/constants'
import { Lock, MapPin, Shield, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { motion, useInView, Variants } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

export const SecuritySection = () => {
  const navigate = useNavigate()
  const [cryptoText, setCryptoText] = useState('')
  const descriptionRef = useRef(null)
  const isInView = useInView(descriptionRef, { once: true, amount: 0.5 })

  const originalText = `We use Shamir's Secret Sharing, the same mathematical approach that protects nuclear launch codes. Your authenticator becomes impossible to unlock without completing your chosen challenges.`

  useEffect(() => {
    if (!isInView) {
      const chars =
        '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*()_+-=[]{}|;:,.<>?'
      setCryptoText(
        originalText
          .split('')
          .map(char => {
            if (char === ' ' || char === '.' || char === ',') return char
            return chars[Math.floor(Math.random() * chars.length)]
          })
          .join('')
      )
    }
  }, [])

  useEffect(() => {
    if (!isInView) return

    const chars =
      '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*()_+-=[]{}|;:,.<>?'
    let iteration = 0
    const maxIterations = 30

    const interval = setInterval(() => {
      setCryptoText(
        originalText
          .split('')
          .map((char, index) => {
            if (char === ' ' || char === '.' || char === ',') return char
            if (index < iteration * (originalText.length / maxIterations)) {
              return originalText[index]
            }
            return chars[Math.floor(Math.random() * chars.length)]
          })
          .join('')
      )

      iteration++

      if (iteration > maxIterations) {
        clearInterval(interval)
        setCryptoText(originalText)
      }
    }, 50)

    return () => clearInterval(interval)
  }, [isInView])

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.15
      }
    }
  }

  const headerVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  }

  const cardVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 30,
      rotateX: -15
    },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  }

  const iconVariants: Variants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 150,
        damping: 12,
        delay: 0.2
      }
    }
  }

  const buttonVariants: Variants = {
    hidden: {
      opacity: 0,
      scale: 0.8
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: 0.6,
        type: 'spring',
        stiffness: 100
      }
    }
  }

  return (
    <section className="from-shire-light to-shire-moss/30 bg-gradient-to-b py-20 md:py-40">
      <motion.div
        className="mx-auto max-w-5xl px-6 text-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.h2
          className="font-heading text-shire-dark mb-8 text-4xl md:text-5xl"
          variants={headerVariants}
        >
          The Security Behind Quest-Lock
        </motion.h2>

        <motion.p
          ref={descriptionRef}
          className="font-body text-shire-bark mx-auto mb-8 max-w-3xl text-xl leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {cryptoText}
        </motion.p>

        <motion.div
          className="mb-12 grid gap-6 md:grid-cols-3"
          variants={containerVariants}
        >
          <motion.div
            className="border-shire-bark/50 bg-shire-light rounded-xl border p-6 shadow-lg"
            variants={cardVariants}
            whileHover={{
              y: -5,
              boxShadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              transition: { duration: 0.3 }
            }}
          >
            <motion.div variants={iconVariants}>
              <Shield className="text-shire-pine mx-auto mb-4 size-10" />
            </motion.div>
            <h3 className="font-heading text-shire-dark mb-2 text-lg">
              Threshold Security
            </h3>
            <p className="text-shire-bark text-sm">
              You decide how many challenges to complete for access. Need 3 out
              of 5? Even if some become impossible, you still have multiple
              paths to unlock your authenticator.
            </p>
          </motion.div>

          <motion.div
            className="border-shire-bark/50 bg-shire-light rounded-xl border p-6 shadow-lg"
            variants={cardVariants}
            whileHover={{
              y: -5,
              boxShadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              transition: { duration: 0.3 }
            }}
          >
            <motion.div variants={iconVariants}>
              <MapPin className="text-shire-pine mx-auto mb-4 size-10" />
            </motion.div>
            <h3 className="font-heading text-shire-dark mb-2 text-lg">
              Physical Barriers
            </h3>
            <p className="text-shire-bark text-sm">
              Your authenticator is encrypted and locked behind real-world
              challenges. The unlock keys are stored on your quests, requiring
              physical actions to retrieve: time delays, mountain hikes, forest
              walks.
            </p>
          </motion.div>

          <motion.div
            className="border-shire-bark/50 bg-shire-light rounded-xl border p-6 shadow-lg"
            variants={cardVariants}
            whileHover={{
              y: -5,
              boxShadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              transition: { duration: 0.3 }
            }}
          >
            <motion.div variants={iconVariants}>
              <Lock className="text-shire-pine mx-auto mb-4 size-10" />
            </motion.div>
            <h3 className="font-heading text-shire-dark mb-2 text-lg">
              Zero Knowledge
            </h3>
            <p className="text-shire-bark text-sm">
              The system is designed so that you are the only person who can
              ever unlock your authenticator, and only after completing your
              challenges.
            </p>
          </motion.div>
        </motion.div>

        <motion.button
          className="bg-shire-pine font-ui text-shire-light hover:bg-shire-bark inline-flex items-center gap-3 rounded-full px-8 py-4 text-lg font-semibold shadow-lg transition-all duration-300 hover:scale-105"
          onClick={() => navigate(appRoutes.howItWorks)}
          variants={buttonVariants}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Sparkles className="size-5" />
          Explore the Interactive Demo
        </motion.button>
      </motion.div>
    </section>
  )
}
